// Time off (PRD §3.13, §4.11, rev. 1.16): vacation and personal/sick balances that mirror payroll's
// accrual rules from the Employee Handbook, Part 5, since payroll has no API or export.
//
// - Accrual per paycheck: salaried people are paid on the last day of each month (6.6667 h a balance),
//   hourly people every other Friday (3.0769 h), 80 h a year either way. 1099 providers have none.
// - Part-time (capacity under 36 h a week, per the handbook's line) accrues pro rata: capacity ÷ 40.
// - Vacation pauses at twice the year's accrual (160 h full-time) and resumes below it; it can't be
//   used in the first 90 days. Personal leave accrues at most 80 h a calendar year and stops at 160 h
//   (80 h part-time).
// - A balance starts from payroll's figure as of a date, when an admin has entered one. Before that it
//   is estimated: accruals from the first day of pay, less the time off reported under 3SVAC or 3SSIC.
//   After it, approved requests come off on their days.
// - Balances are in hours, since partial days are allowed.
//
// Requests and reporting (Chris, 2026-10-29). The records show both what was asked for and what the
// closed time card says was used, since sick time is rarely asked for ahead and vacation often runs
// over or under:
// - Once a week's time card closes, each day's vacation and sick time is read from it: 8 h or more on a
//   day (an all-day event counts as 8 h) records 8 h; less records what was there.
// - Time reported with no request becomes a request record of its own, "Automated based on time card
//   reporting". A request whose days came in over or under gets an adjustment record for the
//   difference, "Adjusted based on time card reporting".
// - A requested day with nothing on the calendar waits in the inbox: they most likely forgot to block
//   it. An admin says whether it was taken.
// - Then payroll gets one email for the person's week (FTEs only), not on approval, so a plan that
//   changed never reaches payroll.

import { addDays, daysBetween, eachDay, isWeekday, lastDayOf, monthOf, yearOf, type Iso } from "./dates";
import { inEffect, isEmployee, live, type Person } from "./people";

export type TimeOffBucket = "vacation" | "personal" | "unpaid";

export const BUCKET_CODE: Readonly<Record<TimeOffBucket, string>> = { vacation: "3SVAC", personal: "3SSIC", unpaid: "3SUTO" };
export const BUCKET_LABEL: Readonly<Record<TimeOffBucket, string>> = { vacation: "Vacation (PTO)", personal: "Personal / sick", unpaid: "Unpaid time off" };

export const ANNUAL_ACCRUAL = 80;
export const MONTHLY_ACCRUAL = ANNUAL_ACCRUAL / 12; // 6.6667
export const BIWEEKLY_ACCRUAL = ANNUAL_ACCRUAL / 26; // 3.0769
export const FULL_TIME_HOURS = 36;
export const VACATION_WAIT_DAYS = 90;
const PERSONAL_CAP_FT = 160;
const PERSONAL_CAP_PT = 80;

export interface TimeOffDay {
  day: Iso;
  hours: number;
}

export const AUTOMATED_NOTE = "Automated based on time card reporting";
export const ADJUSTED_NOTE = "Adjusted based on time card reporting";
/** Reported hours on a day count up to a full day (Chris, 2026-10-29). */
export const FULL_DAY_HOURS = 8;

/** An email to payroll, and the reply (AM). */
export interface PayrollStep {
  emailedAt?: string;
  confirmedAt?: string;
  confirmedBy?: string;
  reply?: string;
  replyFrom?: string;
  replyAt?: string;
  needsReview?: boolean;
}

export interface TimeOffRequest {
  id: string;
  /** Short reference used in the email subject, e.g. "7K2Q" (AM1). */
  ref: string;
  bucket: TimeOffBucket;
  days: TimeOffDay[];
  note?: string;
  status: "requested" | "approved" | "declined" | "cancelled";
  requestedBy: string;
  requestedAt: string;
  decidedBy?: string;
  decidedAt?: string;
  decisionNote?: string;
  /** Warnings shown when it was made (TO3); never blocking. */
  warnings: string[];
  /** On approval: the balance before, the hours taken and the balance after, as sent to payroll (TO5). */
  ledger?: { before: number; deduction: number; after: number };
  /** Requests approved before 2026-10-29, when approval emailed Tom (TO5): that email and his reply. */
  payroll?: PayrollStep;
  /**
   * Where the record came from: asked for ahead (absent, or "request"), recorded from a closed time card
   * with no request ("reported"), or the difference between a request and what was reported
   * ("adjustment", whose hours are signed).
   */
  origin?: "request" | "reported" | "adjustment";
  /** An adjustment: the request it adjusts. */
  adjusts?: string;
}

/** One day of vacation or sick time a closed time card reported, as payroll is told it. */
export interface PayrollLine {
  day: Iso;
  bucket: "vacation" | "personal";
  hours: number;
  /** An approved request covered the day. */
  requested: boolean;
}

/** A closed week, once its time off is recorded: what it showed, and the email to payroll (FTEs only). */
export interface TimeOffWeek {
  monday: Iso;
  recordedAt: string;
  lines: PayrollLine[];
  /** Absent when there was nothing to tell payroll, or the person isn't on payroll. */
  payroll?: PayrollStep & { ref: string };
}

/** An admin's answer for a requested day with nothing on the calendar: was it taken? */
export interface CalendarCheck {
  day: Iso;
  bucket: "vacation" | "personal";
  taken: boolean;
  by: string;
  at: string;
  note?: string;
}

/** Payroll's balances as of a date, typed in by an admin (TO2). */
export interface PayrollBalance {
  id: string;
  asOf: Iso;
  vacation: number;
  personal: number;
  kind: "start" | "check";
  enteredBy: string;
  enteredAt: string;
}

export interface TimeOffDoc {
  id: string;
  personId: string;
  balances: PayrollBalance[];
  requests: TimeOffRequest[];
  /** Closed weeks already recorded (2026-10-29); absent on older documents. */
  weeks?: TimeOffWeek[];
  calendarChecks?: CalendarCheck[];
}

export const timeOffDocId = (personId: string) => `timeoff-${personId}`;

/** A person's paydays in [from, to]: month ends when salaried, every other Friday from the anchor when hourly. */
export function paydays(person: Person, from: Iso, to: Iso, hourlyAnchor: Iso): { day: Iso; perPaycheck: number }[] {
  const out: { day: Iso; perPaycheck: number }[] = [];
  for (let m = monthOf(from); m <= monthOf(to); m = monthOf(addDays(lastDayOf(m), 1))) {
    const end = lastDayOf(m);
    const comp = inEffect(live(person.compRecords), end);
    if (comp?.type === "exempt" && end >= from && end <= to) out.push({ day: end, perPaycheck: MONTHLY_ACCRUAL });
  }
  // Every other Friday, counted from a known payday.
  const offset = ((daysBetween(hourlyAnchor, from) % 14) + 14) % 14;
  for (let d = offset === 0 ? from : addDays(from, 14 - offset); d <= to; d = addDays(d, 14)) {
    const comp = inEffect(live(person.compRecords), d);
    if (comp?.type === "hourly") out.push({ day: d, perPaycheck: BIWEEKLY_ACCRUAL });
  }
  return out.sort((a, b) => a.day.localeCompare(b.day));
}

/** Pro rata for part-time: capacity ÷ 40 under 36 h a week, else 1. */
export function partTimeFactor(person: Person, day: Iso): { factor: number; partTime: boolean } {
  const hours = inEffect(live(person.capacity), day)?.hoursPerWeek ?? 45;
  return hours < FULL_TIME_HOURS ? { factor: hours / 40, partTime: true } : { factor: 1, partTime: false };
}

/** The first day the person was paid as an employee, if ever. */
export function employeeSince(person: Person): Iso | null {
  const first = live(person.compRecords)
    .filter((r) => isEmployee(r.type))
    .map((r) => r.from)
    .sort()[0];
  return first ?? null;
}

export interface BalanceState {
  bucket: "vacation" | "personal";
  /** Balance at the as-of day. */
  balance: number;
  /** Balance less approved time off booked after the as-of day. */
  available: number;
  /** True until an admin enters payroll's figure. */
  estimated: boolean;
  /** Where the balance starts from: payroll's figure, or the first day of pay. */
  since: Iso | null;
  cap: number;
  /** This year's accrual at the current rate, and per paycheck. */
  annual: number;
  perPaycheck: number;
  /** Accrual paused at the cap. */
  atCap: boolean;
  /** Vacation only: the first day it can be used (after 90 days). */
  usableFrom?: Iso;
}

/**
 * A person's balances as of a day. `reported` is time off reported under 3SVAC and 3SSIC (hours per
 * day, by bucket), used only for the estimate before payroll's figure is entered.
 */
export function balancesOf(
  person: Person,
  doc: Pick<TimeOffDoc, "balances" | "requests"> | null,
  asOf: Iso,
  o: { hourlyAnchor: Iso; reported?: { vacation: ReadonlyMap<Iso, number>; personal: ReadonlyMap<Iso, number> } },
): { vacation: BalanceState; personal: BalanceState } | null {
  const since = employeeSince(person);
  if (!since) return null;
  const start = (doc?.balances ?? []).filter((b) => b.kind === "start" && b.asOf <= asOf).sort((a, b) => a.asOf.localeCompare(b.asOf)).at(-1) ?? null;
  const approved = (doc?.requests ?? []).filter((r) => r.status === "approved");
  const booked = (bucket: "vacation" | "personal", from: Iso, to: Iso) =>
    approved.filter((r) => r.bucket === bucket).flatMap((r) => r.days).filter((d) => d.day > from && d.day <= to).reduce((t, d) => t + d.hours, 0);
  const { factor, partTime } = partTimeFactor(person, asOf);
  const vacationCap = 2 * ANNUAL_ACCRUAL * factor;
  const personalCap = partTime ? PERSONAL_CAP_PT : PERSONAL_CAP_FT;

  const run = (bucket: "vacation" | "personal"): BalanceState => {
    const from = start ? start.asOf : addDays(since, -1);
    let balance = start ? start[bucket] : 0;
    let accruedThisYear = 0;
    let year = yearOf(addDays(from, 1));
    // Deductions on each day: approved requests after a payroll figure; reported time off before one.
    const taken = new Map<Iso, number>();
    if (start) {
      for (const r of approved.filter((x) => x.bucket === bucket)) for (const d of r.days) if (d.day > from && d.day <= asOf) taken.set(d.day, (taken.get(d.day) ?? 0) + d.hours);
    } else {
      for (const [d, h] of o.reported?.[bucket] ?? []) if (d > from && d <= asOf) taken.set(d, (taken.get(d) ?? 0) + h);
    }
    const pays = new Map(paydays(person, addDays(from, 1), asOf, o.hourlyAnchor).map((p) => [p.day, p.perPaycheck]));
    let atCap = false;
    for (const d of eachDay(addDays(from, 1), asOf)) {
      if (yearOf(d) !== year) {
        year = yearOf(d);
        accruedThisYear = 0;
      }
      balance -= taken.get(d) ?? 0;
      const pay = pays.get(d);
      if (pay === undefined) continue;
      const f = partTimeFactor(person, d).factor;
      let add = pay * f;
      if (bucket === "personal") add = Math.min(add, Math.max(0, ANNUAL_ACCRUAL * f - accruedThisYear));
      const cap = bucket === "vacation" ? 2 * ANNUAL_ACCRUAL * f : personalCap;
      add = Math.max(0, Math.min(add, cap - balance));
      atCap = balance + add >= cap - 1e-9;
      balance += add;
      accruedThisYear += add;
    }
    const later = booked(bucket, asOf, "9999-12-31");
    const perPaycheck = (paydays(person, asOf, addDays(asOf, 40), o.hourlyAnchor)[0]?.perPaycheck ?? MONTHLY_ACCRUAL) * factor;
    return {
      bucket,
      balance: round(balance),
      available: round(balance - later),
      estimated: !start,
      since: start ? start.asOf : since,
      cap: bucket === "vacation" ? vacationCap : personalCap,
      annual: ANNUAL_ACCRUAL * factor,
      perPaycheck,
      atCap,
      ...(bucket === "vacation" ? { usableFrom: addDays(since, VACATION_WAIT_DAYS) } : {}),
    };
  };
  return { vacation: run("vacation"), personal: run("personal") };
}

const round = (h: number) => Math.round(h * 10_000) / 10_000;

/** The working days between two dates, at a default number of hours each (capacity ÷ 5). */
export function requestDays(person: Person, from: Iso, to: Iso, holidays: ReadonlySet<Iso>): TimeOffDay[] {
  const out: TimeOffDay[] = [];
  for (const d of eachDay(from, to)) {
    if (!isWeekday(d) || holidays.has(d)) continue;
    out.push({ day: d, hours: (inEffect(live(person.capacity), d)?.hoursPerWeek ?? 45) / 5 });
  }
  return out;
}

export interface Blackout {
  from: Iso;
  to: Iso;
  note?: string;
}

/**
 * What to warn about on a request (TO3); none of it blocks. `others` is everyone else's approved days.
 */
export function requestWarnings(
  person: Person,
  r: { bucket: TimeOffBucket; days: readonly TimeOffDay[] },
  today: Iso,
  o: { available: number | null; blackouts: readonly Blackout[]; others: readonly { name: string; day: Iso }[]; holidays: ReadonlySet<Iso> },
): string[] {
  const out: string[] = [];
  if (!r.days.length) return out;
  const first = r.days.map((d) => d.day).sort()[0];
  const hours = r.days.reduce((t, d) => t + d.hours, 0);
  const since = employeeSince(person);
  if (r.bucket === "vacation" && since && first < addDays(since, VACATION_WAIT_DAYS)) out.push(`Vacation can be used after the first 90 days of employment, from ${addDays(since, VACATION_WAIT_DAYS)}.`);
  if (r.bucket === "vacation" && daysBetween(today, first) < 14) out.push("Vacation should be requested at least two weeks ahead.");
  if (r.bucket !== "unpaid" && o.available !== null && hours > o.available + 1e-9) out.push(`This is ${hours} h against ${Math.max(0, o.available)} h available.`);
  for (const b of o.blackouts) if (r.days.some((d) => d.day >= b.from && d.day <= b.to)) out.push(`It falls in a blackout period, ${b.from} to ${b.to}${b.note ? ` (${b.note})` : ""}.`);
  const overlap = [...new Set(o.others.filter((x) => r.days.some((d) => d.day === x.day)).map((x) => x.name))];
  if (overlap.length) out.push(`Also off then: ${overlap.join(", ")}.`);
  const onHoliday = r.days.filter((d) => o.holidays.has(d.day));
  if (onHoliday.length) out.push(`Includes a company holiday (${onHoliday.map((d) => d.day).join(", ")}), which needs no time off.`);
  return out;
}

/** Approved time off as hours per day for one person: what capacity removes (EC12, TO6). */
export function bookedHours(doc: Pick<TimeOffDoc, "requests"> | null): Map<Iso, number> {
  const out = new Map<Iso, number>();
  for (const r of doc?.requests ?? []) if (r.status === "approved") for (const d of r.days) out.set(d.day, (out.get(d.day) ?? 0) + d.hours);
  return out;
}

/** A short reference for email subjects: four characters from an unambiguous alphabet. */
export function shortRef(random: () => number = Math.random): string {
  const abc = "23456789ABCDEFGHJKLMNPQRSTUVWXYZ";
  return Array.from({ length: 4 }, () => abc[Math.floor(random() * abc.length)]).join("");
}

// --- Reporting (Chris, 2026-10-29) --------------------------------------------------------------------

/** A time entry, as far as reporting time off needs it. */
interface ReportedEntry {
  day: Iso;
  hours: number;
  codes: string[];
  resolution?: { codes: string[] };
  allDay?: true;
}

const PAID: readonly ("vacation" | "personal")[] = ["vacation", "personal"];
const r2 = (h: number) => Math.round(h * 100) / 100;

/**
 * Hours reported per day under 3SVAC, 3SSIC and 3SUTO. Vacation and sick are counted as payroll is told
 * them: 8 h or more on a day, or an all-day event, is 8 h; less is what was there. Unpaid is as reported.
 */
export function reportedTimeOff(entries: Iterable<ReportedEntry>): Record<TimeOffBucket, Map<Iso, number>> {
  const out: Record<TimeOffBucket, Map<Iso, number>> = { vacation: new Map(), personal: new Map(), unpaid: new Map() };
  const allDay = { vacation: new Set<Iso>(), personal: new Set<Iso>() };
  const buckets = Object.keys(BUCKET_CODE) as TimeOffBucket[];
  for (const e of entries) {
    const codes = e.resolution?.codes ?? e.codes;
    if (e.resolution && !codes.length) continue; // marked not work time
    for (const code of codes) {
      const bucket = buckets.find((b) => BUCKET_CODE[b] === code);
      if (!bucket) continue;
      if (e.allDay && bucket !== "unpaid") allDay[bucket].add(e.day);
      out[bucket].set(e.day, (out[bucket].get(e.day) ?? 0) + e.hours / codes.length);
    }
  }
  for (const b of PAID) {
    for (const d of allDay[b]) out[b].set(d, FULL_DAY_HOURS);
    for (const [d, h] of out[b]) out[b].set(d, r2(Math.min(FULL_DAY_HOURS, h)));
  }
  return out;
}

/** A requested day with nothing reported on the calendar, waiting for an admin (Chris, 2026-10-29). */
export interface MissingDay {
  day: Iso;
  bucket: "vacation" | "personal";
  hours: number;
}

/**
 * A closed week's vacation and sick time against what was approved ahead. While a requested day has
 * nothing on the calendar and no admin answer, it comes back in `missing` and nothing is recorded: the
 * week waits. Otherwise: the records to add, and the lines to tell payroll.
 */
export function reconcileWeek(
  doc: Pick<TimeOffDoc, "requests" | "calendarChecks"> | null,
  monday: Iso,
  reported: Readonly<Record<"vacation" | "personal", ReadonlyMap<Iso, number>>>,
  o: { at: string; by: string; newRef?: () => string; newId?: () => string },
): { missing: MissingDay[]; records: TimeOffRequest[]; lines: PayrollLine[] } {
  const days = [...eachDay(monday, addDays(monday, 6))];
  const approved = (doc?.requests ?? []).filter((r) => r.status === "approved");
  const checks = doc?.calendarChecks ?? [];
  const missing: MissingDay[] = [];
  const lines: PayrollLine[] = [];
  const unrequested: Record<"vacation" | "personal", TimeOffDay[]> = { vacation: [], personal: [] };
  const deltas = new Map<string, { request: TimeOffRequest; days: TimeOffDay[] }>();
  for (const bucket of PAID) {
    for (const day of days) {
      const onDay = approved.filter((r) => r.bucket === bucket && r.days.some((d) => d.day === day));
      const booked = r2(onDay.flatMap((r) => r.days.filter((d) => d.day === day)).reduce((t, d) => t + d.hours, 0));
      let used = reported[bucket].get(day) ?? 0;
      if (booked > 0 && used === 0) {
        const check = checks.find((c) => c.day === day && c.bucket === bucket);
        if (!check) {
          missing.push({ day, bucket, hours: booked });
          continue;
        }
        used = check.taken ? booked : 0;
      }
      if (used > 0) lines.push({ day, bucket, hours: used, requested: booked > 0 });
      if (booked <= 0) {
        if (used > 0) unrequested[bucket].push({ day, hours: used });
        continue;
      }
      const diff = r2(used - booked);
      if (diff === 0) continue;
      // The difference goes against the request asked for ahead, not an earlier adjustment.
      const request = onDay.find((r) => r.origin !== "adjustment") ?? onDay[0];
      const into = deltas.get(request.id) ?? { request, days: [] };
      into.days.push({ day, hours: diff });
      deltas.set(request.id, into);
    }
  }
  if (missing.length) return { missing, records: [], lines: [] };
  const newRef = o.newRef ?? (() => shortRef());
  const newId = o.newId ?? (() => crypto.randomUUID());
  const stamp = { status: "approved" as const, requestedBy: o.by, requestedAt: o.at, decidedBy: o.by, decidedAt: o.at, warnings: [] };
  const records: TimeOffRequest[] = [];
  for (const bucket of PAID) {
    if (unrequested[bucket].length) records.push({ id: newId(), ref: newRef(), bucket, days: unrequested[bucket], note: AUTOMATED_NOTE, origin: "reported", ...stamp });
  }
  for (const { request, days: d } of deltas.values()) {
    records.push({ id: newId(), ref: newRef(), bucket: request.bucket, days: d, note: ADJUSTED_NOTE, origin: "adjustment", adjusts: request.id, ...stamp });
  }
  lines.sort((a, b) => a.day.localeCompare(b.day) || a.bucket.localeCompare(b.bucket));
  return { missing: [], records, lines };
}
